import React, { ComponentProps, FC, useMemo } from 'react';
import NextLink from 'next/link';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import { resolveReferenceProps, resolveReferenceTarget } from '@loomstateorg/shared-frontend/helpers/reference';
import { Reference } from '@loomstateorg/shared-frontend/types/reference';

export type LinkVariant = 'primary' | 'menu-header' | 'menu-item' | 'breadcrumb';

export interface LinkProps extends ComponentProps<'a'> {
  link?: string | Reference;
  variant?: LinkVariant;
}

type VariantStyle = { [key in LinkVariant]: string };

const variantStyle: VariantStyle = {
  primary: 'text-14 lg:text-16 cursor-pointer text-neutral-500 hover:text-neutral-400',
  'menu-item': 'text-secondary-regular hover:underline hover:underline-offset-2',
  breadcrumb: 'text-14 font-medium text-primary-regular',
  'menu-header': 'text-14 font-medium text-primary-regular cursor-pointer',
};

const Link: FC<LinkProps> = ({ link, children, className = '', variant, title = '', ...linkAttributes }) => {
  const { ['data-testid']: dataTestId = 'atom-link', ...props } = linkAttributes as LinkProps & {
    'data-testid'?: string;
  };

  const linkUrl = useMemo(() => {
    if (!link) return '';
    if (typeof link === 'string') return link;

    return resolveReferenceTarget(link);
  }, [link]);

  const linkProps = useMemo(() => {
    if (!link || typeof link === 'string') return {};

    return resolveReferenceProps(link);
  }, [link]);

  const linkClassNames = useClassNames([variant ? variantStyle[variant] : '', className]);

  // Handle JavaScript URLs separately to avoid React's security blocking
  const isJavaScriptUrl = linkUrl && linkUrl.startsWith('javascript:');

  if (!linkUrl) {
    return (
      <a className={linkClassNames} title={title} data-testid={dataTestId} {...props} {...linkProps}>
        {children}
      </a>
    );
  }

  if (isJavaScriptUrl) {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      // Extract and execute the JavaScript code safely
      const jsCode = linkUrl.replace('javascript:', '').trim();
      try {
        // Use Function constructor for safer evaluation than eval
        new Function(jsCode)();
      } catch (error) {
        console.error('Error executing JavaScript link:', error);
      }
    };

    return (
      <a
        href="#"
        onClick={handleClick}
        className={linkClassNames}
        title={title}
        data-testid={dataTestId}
        {...props}
        {...linkProps}
      >
        {children}
      </a>
    );
  }

  return (
    <NextLink href={linkUrl} data-testid={dataTestId} {...linkProps}>
      <span className={linkClassNames} title={title} {...props}>
        {children}
      </span>
    </NextLink>
  );
};

export default Link;
